// Shared renderer for translate cert-type hubs (NAATI / MFA / Embassy).
// Renders 1,800+ words of unique content per locale using CertType data.
import { Link } from "@tanstack/react-router";
import { ShieldCheck, ArrowRight, CheckCircle2, Globe2, Stamp, ListChecks } from "lucide-react";
import { TrustLayer } from "@/components/TrustLayer";
import CertTypeTripleKeyLinks from "@/components/CertTypeTripleKeyLinks";
import NaatiDoctypeMatrix from "@/components/NaatiDoctypeMatrix";
import NaatiAuthorByline from "@/components/NaatiAuthorByline";
import { TRANSLATION_LANGUAGES } from "@/data/translation-languages";
import type { CertType } from "@/data/translation-certtypes";
import { DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import HubLeadCTA from "@/components/HubLeadCTA";
import { AIAnswerBox } from "@/components/AIAnswerBox";

interface Props {
  cert: CertType;
  locale: "th" | "en";
}

const T = (th: string, en: string, l: "th" | "en") => (l === "th" ? th : en);

export function CertTypeHub({ cert, locale: l }: Props) {
  const audience = l === "th" ? cert.audienceTh : cert.audienceEn;
  const destinations = l === "th" ? cert.destinationsTh : cert.destinationsEn;
  const accepted = l === "th" ? cert.acceptedByTh : cert.acceptedByEn;
  const steps = l === "th" ? cert.stepsTh : cert.stepsEn;
  const faqs = l === "th" ? cert.faqsTh : cert.faqsEn;
  const turnaround = l === "th" ? cert.turnaroundTh : cert.turnaroundEn;
  const heroTitle = l === "th" ? cert.nameTh : cert.nameEn;
  const tagline = l === "th" ? cert.taglineTh : cert.taglineEn;

  return (
    <main className="min-h-screen bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> {l === "th" ? cert.shortTh : cert.shortEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {heroTitle}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{tagline}</p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-white/80">
            <span className="inline-flex items-center gap-1.5"><Stamp className="h-4 w-4 text-gold" /> {T("เริ่ม", "From", l)} ฿{cert.priceFromThb.toLocaleString()}/{T("หน้า", "page", l)}</span>
            <span className="inline-flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-gold" /> {turnaround}</span>
            <span className="inline-flex items-center gap-1.5"><Globe2 className="h-4 w-4 text-gold" /> {destinations.length}+ {T("ปลายทาง", "destinations", l)}</span>
          </div>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity={`NYC ${l === "th" ? cert.shortTh : cert.shortEn}`}>
              {l === "th"
                ? `NYC Language Institute (DBD 0435567000061) ให้บริการ ${cert.nameTh} ครบวงจร — เริ่ม ฿${cert.priceFromThb.toLocaleString()}/หน้า ระยะเวลา ${turnaround} รองรับ ${destinations.length}+ ประเทศปลายทาง โดยทีมนักแปลขึ้นทะเบียนกระทรวงยุติธรรม + NAATI + ทนาย Notary Public ในที่เดียว ลดเวลาดำเนินการ 40-60% เทียบกับการแยกขั้นตอน+`
                : `NYC Language Institute (DBD 0435567000061) provides end-to-end ${cert.nameEn} from THB ${cert.priceFromThb.toLocaleString()}/page with ${turnaround} turnaround, covering ${destinations.length}+ destinations. MoJ-sworn translators, NAATI-certified translators and Notary Public attorneys work under one roof, cutting total processing time by 40-60% versus separate vendors. 99%+ first-submission acceptance.`}
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: T("ราคาเริ่มต้น", "Starting price", l), v: `฿${cert.priceFromThb.toLocaleString()}/${T("หน้า", "page", l)}` },
              { k: T("ระยะเวลา", "Turnaround", l), v: turnaround },
              { k: T("ปลายทาง", "Destinations", l), v: `${destinations.length}+` },
              { k: T("รับรองโดย", "Accepted by", l), v: accepted.slice(0, 2).join(" · ") },
              { k: T("ผ่านรอบแรก", "First-submission", l), v: "99%+" },
              { k: "DBD", v: "0435567000061" },
            ]} />
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="https://line.me/R/ti/p/@nycli" className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy-deep hover:bg-gold/90">
              {T("ขอใบเสนอราคาทาง LINE", "Get a LINE quote", l)} <ArrowRight className="h-4 w-4" />
            </a>
            <Link to={l === "th" ? "/quote" : "/en/quote"} className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-bold text-white hover:bg-white/10">
              {T("กรอกฟอร์มขอราคา", "Request quote form", l)}
            </Link>
          </div>
        </div>
      </section>

      {/* Phase 1 — AI Atomic Answer Box */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 -mt-6">
        <AIAnswerBox
          lang={l}
          id={`ai-answer-${cert.slug}`}
          question={
            l === "th"
              ? `${cert.nameTh} ราคาเท่าไร และใช้เวลานานแค่ไหน?`
              : `How much does ${cert.nameEn} cost and how long does it take?`
          }
          answer={
            l === "th"
              ? `${cert.nameTh} ที่ NYC Language Institute เริ่มต้น ฿${cert.priceFromThb.toLocaleString()}/หน้า ระยะเวลา ${turnaround} ครอบคลุม ${destinations.length}+ ประเทศปลายทาง ผ่านรอบแรก 99%+ โดยนักแปล/ทนายที่ขึ้นทะเบียนกับหน่วยงานทางการ`
              : `${cert.nameEn} at NYC Language Institute starts at THB ${cert.priceFromThb.toLocaleString()}/page with ${turnaround} turnaround, covers ${destinations.length}+ destinations and a 99%+ first-submission acceptance rate, performed by officially registered translators and notaries.`
          }
          stats={[
            { label: l === "th" ? "ราคาเริ่ม" : "From", value: `฿${cert.priceFromThb.toLocaleString()}` },
            { label: l === "th" ? "ระยะเวลา" : "Turnaround", value: turnaround },
            { label: l === "th" ? "ปลายทาง" : "Destinations", value: `${destinations.length}+` },
            { label: l === "th" ? "ผ่านรอบแรก" : "First-pass", value: "99%+" },
          ]}
          sourceUrl={`https://nyclanguageinstitute.com${l === "en" ? "/en" : ""}/translate/${cert.slug}`}
        />
      </section>



      {/* INTRO PROSE — adds ~400 words */}
      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:text-navy-deep">
          <h2>{T(`ทำไมต้องใช้บริการ ${cert.shortTh} จาก NYC?`, `Why choose NYC for ${cert.shortEn}?`, l)}</h2>
          <p>
            {l === "th"
              ? `NYC Language Institute (บริษัท เอ็นวายซี ลีเกิล แอนด์ โนตารี เซอร์วิส จำกัด — DBD 0435567000061) ให้บริการแปลและรับรองคำแปลในไทยตั้งแต่ปี 2010 ทีมนักแปลของเรามากกว่า 30 คน ครอบคลุม 19+ ภาษา ทำงานคู่กับนักแปลขึ้นทะเบียนกระทรวงยุติธรรม (Sworn Translator), NAATI Certified Translator สำหรับออสเตรเลีย-นิวซีแลนด์ และทนาย Notary Public (Notarial Services Attorney) จากสภาทนายความ — ทำให้เราสามารถดำเนินการรับรอง ${cert.shortTh} ได้ครบวงจรในที่เดียว ไม่ต้องเดินทางหลายหน่วยงาน ลดเวลาดำเนินการได้ 40-60% เมื่อเทียบกับการแยกขั้นตอน`
              : `NYC Language Institute (NYC Legal & Notary Service Co., Ltd. — DBD 0435567000061) has provided certified translation services in Thailand since 2010. Our roster of 30+ translators covers 19+ languages and works alongside Thai MoJ Sworn Translators, NAATI Certified Translators for Australia and New Zealand, and Notary Public attorneys credentialed by the Lawyers Council of Thailand. That depth lets us run the entire ${cert.shortEn} chain in one place—cutting end-to-end time by 40–60% versus chasing each step separately.`}
          </p>
          <p>
            {l === "th"
              ? `การรับรองแบบ ${cert.shortTh} ไม่ใช่แค่การประทับตรา — แต่เป็นการยืนยันว่าคำแปลตรงตามต้นฉบับ ลายเซ็นและตราของหน่วยงานไทยถูกต้อง และเอกสารพร้อมใช้ในกระบวนการกฎหมายของประเทศปลายทาง หากเอกสารใดเอกสารหนึ่งบกพร่อง (เช่น ทะเบียนบ้านที่ไม่ได้คัดใหม่ภายใน 3 เดือน, สูติบัตรที่ตัวอักษรเลือนราง, หนังสือรับรองบริษัทที่หมดอายุ) จะถูกปฏิเสธทั้งชุด ทำให้เสียเวลาและค่าใช้จ่ายซ้ำ ทีม QA ของ NYC ตรวจสอบเอกสารต้นฉบับก่อนเริ่มงานทุกครั้ง และแจ้งจุดที่ต้องคัดใหม่ก่อนคิดเงิน`
              : `${cert.shortEn} certification is more than a stamp—it confirms that the translation matches the original, that Thai authorities' signatures and seals are valid, and that the document is ready for the destination's legal process. A single defect (an out-of-date district extract, an illegible birth certificate, a stale corporate affidavit) can sink the whole packet and cost you a second round. NYC's QA team reviews every original before work begins and tells you what to refresh before we invoice.`}
          </p>
          <p>
            {l === "th"
              ? `ลูกค้าหลักของบริการนี้ ได้แก่: ${audience.slice(0, 3).join(" / ")} และผู้ใช้งานเฉพาะทางอีกหลายกลุ่ม เราเข้าใจดีว่าทุกเคสมีเดดไลน์ — บริการ Express 24 ชั่วโมงและ Same-day MFA สำหรับเคสที่เร่งจริง ๆ พร้อมพิกัดติดตามสถานะผ่าน LINE @nycli ตลอด 24 ชั่วโมง`
              : `Primary use cases include ${audience.slice(0, 3).join(" / ")}, plus several specialist segments. We know every case has a deadline—24-hour express translation and same-day MFA legalization are available for genuinely urgent jobs, with live status updates on LINE @nycli around the clock.`}
          </p>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="bg-slate-50 py-12 sm:py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{T("เหมาะกับใคร", "Who this is for", l)}</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {audience.map((a, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <p className="text-sm text-slate-700">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{T("ขั้นตอนการทำงาน", "How it works", l)}</h2>
        <ol className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <li key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{T("ขั้นตอน", "Step", l)} {i + 1}</div>
              <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{s.name}</div>
              <p className="mt-2 text-sm text-slate-600">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* DESTINATIONS + ACCEPTED BY */}
      <section className="bg-slate-50 py-12 sm:py-16">
        <div className="mx-auto grid max-w-5xl gap-8 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">{T("ใช้ที่ไหนได้บ้าง", "Where it's used", l)}</h2>
            <ul className="mt-4 space-y-2">
              {destinations.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">{T("หน่วยงานที่ยอมรับ", "Accepted by", l)}</h2>
            <ul className="mt-4 space-y-2">
              {accepted.map((d, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* LANGUAGE PAIRS — extends content + internal linking */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{T("คู่ภาษาที่รองรับ", "Supported language pairs", l)}</h2>
        <p className="mt-2 text-sm text-slate-600">
          {l === "th"
            ? `${cert.shortTh} ใช้คู่กับการแปลภาษาเป้าหมายของแต่ละประเทศ — เลือกภาษาที่ต้องการแปลด้านล่าง ทุกหน้ามีตัวอย่างเอกสาร ราคาเริ่มต้น และ FAQ เฉพาะภาษา`
            : `${cert.shortEn} pairs with translation into each destination's official language. Pick a pair below for samples, starting prices, and language-specific FAQs.`}
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TRANSLATION_LANGUAGES.slice(0, 12).map((lang) => (
            <Link
              key={lang.slug}
              to="/translate/$language"
              params={{ language: lang.slug }}
              className="group rounded-xl border border-slate-200 bg-white p-4 transition hover:border-gold/50 hover:shadow-md"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{lang.nameEn}</div>
              <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{T(`แปล${lang.nameTh}`, `${lang.nameEn} translation`, l)}</div>
              <div className="mt-1 text-xs text-slate-500">{T("เริ่ม", "From", l)} ฿{lang.priceFromTh.toLocaleString()}/{T("หน้า", "page", l)}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* BUNDLED SERVICES PROSE — ~250 words */}
      <section className="bg-navy-deep/[0.03] py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:text-navy-deep">
            <h2>{T("บริการเสริมที่นิยมจัดเป็นแพ็คเกจ", "Popular bundled services", l)}</h2>
            <p>
              {l === "th"
                ? `ลูกค้าส่วนใหญ่ของ ${cert.shortTh} จะใช้บริการเสริมต่อไปนี้พร้อมกันเพื่อจบในที่เดียว: (1) Notary Public รับรองลายเซ็น/สำเนา/หนังสือมอบอำนาจ โดยทนายขึ้นทะเบียนสภาทนายความ ดูบริการทั้งหมดที่ `
                : `Customers using ${cert.shortEn} typically bundle these companion services to close out the entire chain in one engagement: (1) Notary Public certification of signatures, copies, or powers of attorney by a Lawyers Council-licensed attorney—see `}
              <Link to={l === "th" ? "/notary" : "/en/notary"} className="text-amber-700 underline">{T("หน้า Notary Public", "the Notary Public hub", l)}</Link>
              {l === "th"
                ? `, (2) Apostille / Legalization สำหรับใช้ในต่างประเทศ ดูที่ `
                : `, (2) Apostille and consular legalization—see `}
              <Link to={l === "th" ? "/apostille" : "/en/apostille"} className="text-amber-700 underline">{T("หน้า Apostille", "the Apostille hub", l)}</Link>
              {l === "th"
                ? `, (3) บริการยื่นวีซ่าและจัดเตรียมเอกสารวีซ่า ดูที่ `
                : `, (3) Visa filing and document prep—see `}
              <Link to={l === "th" ? "/visa" : "/en/visa"} className="text-amber-700 underline">{T("หน้า Visa Services", "the Visa Services hub", l)}</Link>
              {T(" และ (4) บริการจดทะเบียนสมรสต่างชาติ ดูที่ ", " and (4) Cross-border marriage registration—see ", l)}
              <Link to={l === "th" ? "/marriage" : "/en/marriage"} className="text-amber-700 underline">{T("หน้า Marriage", "the Marriage hub", l)}</Link>.
            </p>
            <p>
              {l === "th"
                ? `การจัดบริการแบบแพ็คเกจช่วยลดต้นทุนได้ 15-25% และลดเวลาดำเนินการเฉลี่ย 5-7 วันทำการ เพราะทีม NYC สามารถจองคิวสถานทูตล่วงหน้าและส่งเอกสารต่อระหว่างขั้นตอนได้โดยไม่ต้องให้ลูกค้าเดินทาง พิจารณาความคุ้มค่าก่อนจองได้ผ่านทีม Account Manager ที่ LINE @nycli`
                : `Packaging these services together reduces cost by 15–25% and shaves 5–7 business days from the timeline, because NYC can pre-book embassy slots and hand documents off between steps without you stopping by. Discuss the right bundle with an Account Manager on LINE @nycli before you commit.`}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{T("คำถามที่พบบ่อย", "Frequently asked questions", l)}</h2>
        <div className="mt-6 space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="group rounded-xl border border-slate-200 bg-white p-5 open:border-amber-300 open:bg-amber-50/30">
              <summary className="cursor-pointer list-none font-display text-base font-extrabold text-navy-deep">{f.q}</summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {cert.slug === "naati" && <NaatiDoctypeMatrix lang={l} />}
      {cert.slug === "naati" && <NaatiAuthorByline lang={l} />}

      <CertTypeTripleKeyLinks mode={l} certShortTh={cert.shortTh} certShortEn={cert.shortEn} />

      <HubLeadCTA locale={l} service={`${cert.shortEn} — ${cert.shortTh}`} />

      <TrustLayer variant="compact" />
    </main>
  );
}
